import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useLMS } from '../../context/LMSContext';

const ResetPasswordScreen = () => {
  const { setIsPasswordRecovery, isDarkMode } = useLMS();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) throw updateError;

      // Also mark password as set in profile
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('profiles')
          .update({ password_set: true, updated_at: new Date().toISOString() })
          .eq('id', user.id);
      }

      setSuccess(true);
      
      // After 2 seconds, clear recovery mode and let the app proceed
      setTimeout(() => {
        setIsPasswordRecovery(false);
      }, 2000);
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`login-page ${isDarkMode ? 'dark' : ''}`} style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{
        maxWidth: 440,
        width: '100%',
        margin: '0 auto',
        padding: 32,
        background: isDarkMode ? '#1e1e2f' : '#ffffff',
        borderRadius: 12,
        boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img 
            src="https://cdn.shopify.com/s/files/1/0659/6208/2392/files/codebreaker.png?v=1768442571" 
            alt="Code Breaker NCLEX"
            style={{ maxWidth: 200, height: 'auto', marginBottom: 16 }}
          />
          <h2 style={{ margin: '0 0 8px 0', color: isDarkMode ? '#fff' : '#1a1a2e' }}>Reset Your Password</h2>
          <p style={{ margin: 0, color: isDarkMode ? '#aaa' : '#666', fontSize: '0.95em' }}>
            Enter your new password below.
          </p>
        </div>

        {success ? (
          <div style={{
            background: '#e8f5e9',
            border: '1px solid #4caf50',
            color: '#2e7d32',
            padding: '16px',
            borderRadius: 6,
            textAlign: 'center',
            fontSize: '1em',
          }}>
            <p style={{ margin: 0, fontWeight: 600 }}>Password updated successfully!</p>
            <p style={{ margin: '8px 0 0 0', fontSize: '0.9em' }}>Redirecting to your dashboard...</p>
          </div>
        ) : (
          <>
            {error && (
              <div style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#dc2626',
                padding: '10px 14px',
                borderRadius: 6,
                marginBottom: 16,
                fontSize: '0.9em'
              }}>
                {error}
              </div>
            )}

            <form onSubmit={handleResetPassword}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: isDarkMode ? '#ccc' : '#333', fontSize: '0.9em' }}>
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter your new password"
                  required
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: 6,
                    border: `1px solid ${isDarkMode ? '#444' : '#ddd'}`,
                    fontSize: '1em',
                    background: isDarkMode ? '#2a2a3d' : '#fff',
                    color: isDarkMode ? '#fff' : '#333',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: isDarkMode ? '#ccc' : '#333', fontSize: '0.9em' }}>
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  required
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: 6,
                    border: `1px solid ${isDarkMode ? '#444' : '#ddd'}`,
                    fontSize: '1em',
                    background: isDarkMode ? '#2a2a3d' : '#fff',
                    color: isDarkMode ? '#fff' : '#333',
                    boxSizing: 'border-box',
                  }}
                />
                <p style={{ margin: '6px 0 0 0', color: isDarkMode ? '#888' : '#999', fontSize: '0.8em' }}>
                  Must be at least 6 characters
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#4f46e5',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: '1em',
                  fontWeight: 600,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting ? 0.7 : 1,
                }}
              >
                {isSubmitting ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordScreen;
